// @flow strict
import * as React from 'react'
import { useState, useEffect } from 'react'
import ReactGA from 'react-ga'
import { Button } from '@mondough/public-web-components'
import USAButton from '../Button/USAButton'

const isWindowDefined = typeof window !== 'undefined'

const navigate = (location: string) => {
  if (isWindowDefined) {
    window.location = location
  }
}

const triggerFacebookTrackEvent = (): void => {
  if (isWindowDefined && window.fbq) {
    window.fbq('track', 'InitiateCheckout', { url: window.location.pathname })
  }
}

const isAdjustLink = (href: string): boolean =>
  href.indexOf('https://app.adjust.com') === 0

const isDeeplink = (href: string): boolean =>
  href.indexOf('https://monzo.com/-deeplinks/') === 0

const furnishHref = (href: string): string => {
  if (isAdjustLink(href)) {
    const hasQueryParams = href.indexOf('?') > -1
    const encodedPathname = encodeURIComponent(window.location.pathname)

    return `${href}${
      hasQueryParams ? '&' : '?'
    }monzo_referrer=${encodedPathname}`
  }

  if (isDeeplink(href)) {
    const encodedPathname = encodeURIComponent(window.location.pathname)
    const hasQueryParams = href.indexOf('?') > -1
    return `${href}${hasQueryParams ? '&' : '?'}referrer=${encodedPathname}`
  }

  return href
}

type TrackEventsProps = {
  category?: string,
  action?: string,
  label?: string,
  children:
    | React.Element<'a'>
    | React.Element<typeof Button>
    | React.Element<typeof USAButton>,
}

const TrackEvents = ({
  category: eventCategory,
  action: eventAction,
  label: eventLabel,
  children,
}: TrackEventsProps) => {
  const [href, setHref] = useState(children.props.href)

  // In order for the furnish links to modify the href
  // we need to be able to access the window object.
  // To do that we need to wait for the component to mount.
  useEffect(() => {
    const { href } = children.props
    if (href) {
      setHref(furnishHref(href))
      // We update the href a second time once we know the GA client ID.
      // If a user is running adblocker the callback never gets called and doesn't throw an error
      // So we have to chain up the updating of the href 😞

      // avoid tracking in-page links
      if (!href.startsWith('#')) {
        ReactGA.ga(tracker => {
          const ga_client_param = `google_client_id=${tracker.get('clientId')}`
          const furnishedHref = furnishHref(href)
          // if there's no query string already on the href, precede the google_client_id with a question mark not an ampersand
          setHref(
            `${furnishedHref}${
              furnishedHref.indexOf('?') < 0
                ? `?${ga_client_param}`
                : `&${ga_client_param}`
            }`,
          )
        })
      }
    }
  }, [children.props, setHref])

  return React.cloneElement(children, {
    href,
    onClick: evt => {
      evt.preventDefault()

      if (href && isAdjustLink(href)) {
        // We are using the existance of adjust links to identify download buttons
        // that take you directly to the app / play store to download.
        // We only want to fire Facebook events for those buttons.
        triggerFacebookTrackEvent()
      }

      const errorTimeout = setTimeout(() => {
        if (href) navigate(href)
      }, 250)

      if (eventCategory && eventAction && eventLabel) {
        ReactGA.ga('send', {
          hitCallback: () => {
            clearTimeout(errorTimeout)
            if (href) navigate(href)
          },
          hitType: 'event',
          transport: 'beacon',
          eventCategory,
          eventAction,
          eventLabel,
        })
      }
    },
  })
}

export default TrackEvents
